import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bus, Image as ImageIcon, Receipt, UserPlus, Users, LogOut, Loader2, HardHat } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Project {
  id: string;
  code: string;
  nameEn: string;
}

export default function SupervisorHomePage() {
  const { user, logout } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<Project>('/api/v1/construction/default')
      .then((r) => setProject(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (!project) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <div className="text-sm text-muted-foreground text-center">No project assigned. Ask the admin to set up your project.</div>
      <Button variant="outline" onClick={logout} className="gap-2">
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );

  const tile = (to: string, icon: React.ReactNode, label: string, primary = false) => (
    <Link
      to={to}
      className={`block rounded-xl p-5 transition-colors ${
        primary
          ? 'bg-foreground text-background hover:bg-foreground/90'
          : 'border border-border bg-card hover:bg-muted/40'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">{icon}</div>
        <div className="text-base font-medium">{label}</div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{user?.fullNameEn ?? user?.code ?? 'Supervisor'}</div>
              <div className="text-xs text-muted-foreground">{project.code}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-2">
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Today</div>
        {tile(
          `/dashboard/construction/projects/${project.id}/attendance`,
          <Bus className="h-6 w-6" />,
          "Today's attendance",
          true,
        )}

        <div className="text-xs uppercase tracking-wider text-muted-foreground mt-5 mb-1">Quick actions</div>
        {tile(
          `/dashboard/construction/projects/${project.id}/performance`,
          <ImageIcon className="h-5 w-5 text-muted-foreground" />,
          'Add work photo',
        )}
        {tile(
          `/dashboard/construction/projects/${project.id}/expenses`,
          <Receipt className="h-5 w-5 text-muted-foreground" />,
          'Log expense',
        )}
        {tile(
          `/dashboard/construction/projects/${project.id}/attendance`,
          <UserPlus className="h-5 w-5 text-muted-foreground" />,
          'Add new worker',
        )}

        <div className="text-xs uppercase tracking-wider text-muted-foreground mt-5 mb-1">Reference</div>
        {tile(
          `/dashboard/construction`,
          <Users className="h-5 w-5 text-muted-foreground" />,
          'Workers list',
        )}
      </main>
    </div>
  );
}
